/**
 * Where this chat is running, and who owns the DOM around it.
 *
 * The app has three hosts:
 *
 *   'spa'     — chat.{zone} in a browser tab. Owns the document.
 *   'iframe'  — the same SPA inside the embed loader's frame. Owns its document.
 *   'element' — mounted into a school's page as <reachout-chat>. Owns nothing:
 *               the URL, <html> class list, document.body and window event bus
 *               all belong to the school.
 *
 * Everything that used to be read from `window.location.search` or written to
 * `document.documentElement` goes through here instead, so the element can
 * supply its own answers without every route and component learning about
 * custom elements.
 *
 * The module-level singleton exists for the handful of non-React callers (and
 * for module-eval-time reads). React code should prefer `useEmbedContext()`,
 * which is what makes more than one element on a page tractable.
 */

import { createContext, useContext } from 'react';
import type { EmbedMode } from '@/lib/embed-protocol';
import type { TransportMode } from '@/types';

export type EmbedSource = 'spa' | 'iframe' | 'element';

export interface EmbedContextValue {
	source: EmbedSource;
	/** Distinguishes two elements on one page — used to key the event bus. */
	instanceId: string;

	/** Endpoint `Code__c`. */
	district: string | null;
	/** How the chat is presented. The element always behaves as 'inline'. */
	embedMode: EmbedMode | null;
	/** A *request*, not an entitlement — see resolveTransportMode. */
	requestedChatMode?: TransportMode;
	/** Raw semicolon-delimited Features__c, as chat-mode.ts expects it. */
	features?: string;

	/**
	 * District config the element carries in its attributes.
	 *
	 * In the iframe these come from the endpoint-status probe. An element has no
	 * route to that probe — it runs on the school's origin and the endpoint is
	 * neither CORS-open nor, today, provisioned at all — so the generator bakes
	 * them into the snippet instead. See hooks/use-endpoint-status.ts.
	 */
	inviteRequired?: boolean;
	greeting?: string;
	privacyNotice?: string;
	chatInfoText?: string;
	accent?: string;
	launcherHeight?: number;
	theme?: 'light' | 'dark';
	/** Resolved homework entitlement. See lib/homework/flag.ts. */
	homework?: boolean;
	/** Explicit "open in full page" destination. See lib/full-page-url.ts. */
	fullPageUrl?: string;

	/**
	 * Element that carries the `.dark` class.
	 *
	 * Tailwind's dark variant is `&:is(.dark *)`, which matches a *descendant*
	 * of `.dark`. In the shadow root that has to be a wrapper inside the root —
	 * `document.documentElement` is outside the boundary and `:is()` cannot
	 * cross it, so setting the class there would silently do nothing.
	 */
	themeRoot?: HTMLElement;
	/** Where portals (tooltip, drag overlay) render. */
	portalContainer?: HTMLElement;
	/** Scope for drag-and-drop listeners, so a drag elsewhere on the school's page is ignored. */
	dropTarget?: HTMLElement;
	/** Per-instance replacement for `window` as a CustomEvent bus. */
	bus: EventTarget;
	/** Re-mount instead of `window.location.reload()`, which would reload the school's page. */
	onReload?: () => void;
}

function readSearchParam(name: string): string | null {
	if (typeof window === 'undefined') return null;
	try {
		return new URLSearchParams(window.location.search).get(name);
	} catch {
		return null;
	}
}

function toEmbedMode(value: string | null): EmbedMode | null {
	return value === 'inline' || value === 'floating' || value === 'ambient' ? value : null;
}

function toTransportMode(value: string | null): TransportMode | undefined {
	return value === 'reachout-ai' || value === 'classic-chat' ? value : undefined;
}

let instanceCounter = 0;

/** Monotonic id. No Math.random — deterministic ids keep test output stable. */
export function nextEmbedInstanceId(prefix: string): string {
	instanceCounter += 1;
	return `${prefix}-${instanceCounter}`;
}

/**
 * The document-owning context: the SPA in a tab, or the SPA inside the loader's
 * iframe. Reads exactly what the app read before this module existed.
 */
export function createDocumentEmbedContext(): EmbedContextValue {
	let embedded = false;
	if (typeof window !== 'undefined') {
		try {
			embedded = window.self !== window.top;
		} catch {
			embedded = true;
		}
	}
	return {
		source: embedded ? 'iframe' : 'spa',
		instanceId: 'document',
		district: readSearchParam('d'),
		embedMode: toEmbedMode(readSearchParam('embed')),
		requestedChatMode: toTransportMode(readSearchParam('mode')),
		features: readSearchParam('features') ?? undefined,
		bus: typeof window !== 'undefined' ? window : new EventTarget(),
	};
}

let active: EmbedContextValue | undefined;

/**
 * The active context for non-React callers.
 *
 * Lazily built so that importing this module never touches `window` — the
 * router codegen and the unit tests both import route files outside a browser.
 */
export function getEmbedContext(): EmbedContextValue {
	if (!active) active = createDocumentEmbedContext();
	return active;
}

/** Called by the element as it mounts. Returns a disposer that restores the previous value. */
export function setActiveEmbedContext(ctx: EmbedContextValue): () => void {
	const previous = active;
	active = ctx;
	return () => {
		if (active === ctx) active = previous;
	};
}

/** Test seam. */
export function _resetEmbedContextForTests(): void {
	active = undefined;
}

const EmbedContextReact = createContext<EmbedContextValue | null>(null);

export const EmbedContextProvider = EmbedContextReact.Provider;

export function useEmbedContext(): EmbedContextValue {
	return useContext(EmbedContextReact) ?? getEmbedContext();
}

/** Where the `.dark` class goes. */
export function themeRootOf(ctx: EmbedContextValue): HTMLElement | null {
	if (ctx.themeRoot) return ctx.themeRoot;
	return typeof document === 'undefined' ? null : document.documentElement;
}

/** Where portals render. */
export function portalContainerOf(ctx: EmbedContextValue): HTMLElement | null {
	if (ctx.portalContainer) return ctx.portalContainer;
	return typeof document === 'undefined' ? null : document.body;
}

/** What drag listeners attach to. */
export function dropTargetOf(ctx: EmbedContextValue): HTMLElement | Window | null {
	if (ctx.dropTarget) return ctx.dropTarget;
	return typeof window === 'undefined' ? null : window;
}

/** True when the chat is mounted into a page we do not own. */
export function isInPage(ctx: EmbedContextValue): boolean {
	return ctx.source === 'element';
}
